import React from "react";
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
  MDBIcon,
  MDBSpinner,
} from "mdb-react-ui-kit";

import { doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";

/**
 * BatchZonesModal — asignar o desasignar zonas de sala a un lote de entradas.
 *
 * La asociación entre entradas y butacas se resuelve comparando
 * ticket.ticketNumber con seat.seatNumber (igualdad de cadenas).
 *
 * Props:
 *  - show: boolean
 *  - batch: TicketBatchDTO (con assignedZones cargadas)
 *  - idVenue: integer — sala asignada a la función
 *  - onClose: () => void
 *  - onSaved: () => void — recargar lotes en el padre
 */
class BatchZonesModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      zones: [],       // zonas disponibles en la sala
      assigned: new Set(), // idZone asignados al lote
      loading: false,
      saving: null,    // idZone que se está procesando (para spinner individual)
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.show !== this.props.show && this.props.show) {
      this.load();
    }
  }

  load = () => {
    const { idVenue, batch } = this.props;
    if (!idVenue || !batch) return;
    this.setState({ loading: true });
    doFetchJson("TEAN", "getVenue/" + idVenue)
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          const zones = (json.DATA.zones || [])
            .filter((z) => !z.isStage) // excluir zonas escenario
            .map((z) => ({
            idZone: z.idZone,
            name: z.name,
            color: z.color,
            seatCount: (z.seats || []).filter((s) => s.enabled).length,
            numberingScheme: z.numberingScheme,
          }));
          // Inicializar el Set con las zonas ya asignadas al lote
          const assigned = new Set(
            (batch.assignedZones || []).map((z) => z.idZone)
          );
          this.setState({ zones, assigned, loading: false });
        } else {
          toast.show({ message: "No se pudo cargar la sala", type: "danger" });
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        toast.show({ message: "Error de red", type: "danger" });
        this.setState({ loading: false });
      });
  };

  handleClose = () => this.props.onClose && this.props.onClose();

  toggleZone = (idZone) => {
    const { assigned } = this.state;
    const { batch } = this.props;
    const isAssigned = assigned.has(idZone);
    const endpoint = isAssigned
      ? `removeZoneFromBatch/${batch.idBatch}/${idZone}`
      : `assignZoneToBatch/${batch.idBatch}/${idZone}`;
    const method = isAssigned ? "DELETE" : "POST";

    this.setState({ saving: idZone });
    doFetchJson("TEAN", endpoint, { method })
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          const next = new Set(assigned);
          if (isAssigned) next.delete(idZone);
          else next.add(idZone);
          this.setState({ assigned: next });
          if (this.props.onSaved) this.props.onSaved();
        } else {
          toast.show({ message: (json && json.MESSAGE) || "Error al actualizar la zona", type: "danger" });
        }
      })
      .catch(() => toast.show({ message: "Error de red", type: "danger" }))
      .finally(() => this.setState({ saving: null }));
  };

  render() {
    const { show, batch } = this.props;
    const { zones, assigned, loading, saving } = this.state;

    return (
      <MDBModal show={show} onHide={this.handleClose} tabIndex="-1">
        <MDBModalDialog centered>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                <MDBIcon fas icon="vector-square" className="me-2" />
                Zonas del lote "{batch ? batch.name : ""}"
              </MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={this.handleClose} />
            </MDBModalHeader>
            <MDBModalBody>
              <p className="text-muted small mb-3">
                Activa las zonas de sala que corresponden a este lote. La asignación se resuelve
                por número: la entrada #142 se vincula a la butaca con número "142" en la zona asignada.
              </p>

              {loading ? (
                <div className="text-center py-4">
                  <MDBSpinner />
                </div>
              ) : zones.length === 0 ? (
                <div className="text-muted fst-italic text-center py-3">
                  La sala no tiene zonas configuradas todavía.
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {zones.map((z) => {
                    const isOn = assigned.has(z.idZone);
                    const isSaving = saving === z.idZone;
                    return (
                      <div
                        key={z.idZone}
                        className={`d-flex align-items-center justify-content-between p-2 rounded border${isOn ? " border-primary bg-primary bg-opacity-10" : ""}`}
                        style={{ cursor: isSaving ? "wait" : "pointer" }}
                        onClick={() => !isSaving && this.toggleZone(z.idZone)}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <span
                            style={{
                              width: 14,
                              height: 14,
                              borderRadius: 3,
                              backgroundColor: z.color || "#ccc",
                              flexShrink: 0,
                            }}
                          />
                          <div>
                            <div className="fw-semibold small">{z.name}</div>
                            <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                              {z.seatCount} butacas habilitadas
                              {z.numberStart != null && z.numberEnd != null && !isNaN(z.numberStart) && !isNaN(z.numberEnd)
                                ? ` · nº ${z.numberStart}–${z.numberEnd}`
                                : ""}
                            </div>
                          </div>
                        </div>
                        <div>
                          {isSaving ? (
                            <MDBSpinner size="sm" />
                          ) : (
                            <div
                              className={`form-check-input m-0${isOn ? " bg-primary border-primary" : ""}`}
                              style={{
                                width: 22,
                                height: 22,
                                borderRadius: 4,
                                border: "2px solid",
                                borderColor: isOn ? "#0d6efd" : "#adb5bd",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: isOn ? "#0d6efd" : "transparent",
                              }}
                            >
                              {isOn && (
                                <MDBIcon fas icon="check" style={{ color: "#fff", fontSize: "0.7rem" }} />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" outline onClick={this.handleClose}>
                Cerrar
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    );
  }
}

export default BatchZonesModal;
